import Card from "@mui/material/Card";
import SoftBox from "../../../components/SoftBox";
import {useNavigate, useParams} from "react-router-dom";
import {deleteStaffImage, getStaffById, getStaffImages, uploadStaffImages} from "../../../api/staff/staffApiClient";
import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Fab,
    Grid,
    IconButton,
    Typography
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {AddPhotoAlternate, ArrowBack, Close, Delete, ZoomIn} from "@mui/icons-material";
import StaffInfo from "./StaffInfo";
import AuthenticatedImage from "../../../components/AuthenticatedImage/AuthenticatedImage";

function StaffDetailPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const {staffId} = useParams();
    const [staff, setStaff] = useState({});
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [uploading, setUploading] = useState(false);

    const loadStaffDetails = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await getStaffById(staffId);
            const staffData = res.data;
            setStaff(staffData);

            // Load staff images
            try {
                const imagesRes = await getStaffImages(staffId);
                const imagesData = imagesRes.data || [];
                console.log('Loaded staff images:', imagesData);
                console.log('Image URLs:', imagesData.map(img => ({
                    imageId: img.imageId,
                    publicImageUrl: img.publicImageUrl,
                    imageUrl: img.imageUrl
                })));
                setImages(imagesData);

                // Images loaded successfully
            } catch (imageError) {
                console.error("Failed to load staff images:", imageError);
                setImages([]);
            }
        } catch (error) {
            console.error("Failed to load staff details:", error);
            setError("Failed to load staff details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (staffId) {
            loadStaffDetails();
        }
    }, [staffId]); // loadStaffDetails is not stable, so removed from dependencies

    // Debug effect to monitor images state changes
    useEffect(() => {
        console.log('Images state changed:', images);
    }, [images]); // Remove loadStaffDetails from dependencies

    const handleImageClick = (image) => {
        setSelectedImage(image);
    };

    const handleCloseDialog = () => {
        setSelectedImage(null);
    };

    const handleDeleteClick = (image, event) => {
        event.stopPropagation();
        setImageToDelete(image);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteStaffImage(staffId, imageToDelete.imageId);
            setImages(prev => prev.filter(img => img.imageId !== imageToDelete.imageId));
            setDeleteDialogOpen(false);
            setImageToDelete(null);
        } catch (error) {
            setError("Failed to delete image: " + error.message);
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        try {
            setUploading(true);
            const formData = new FormData();
            files.forEach(file => {
                formData.append("images", file);
            });

            await uploadStaffImages(staffId, formData);
            try {
                const imagesRes = await getStaffImages(staffId);
                const imagesData = imagesRes.data || [];
                setImages(imagesData);
            } catch (imageError) {
                console.error("Failed to reload staff images:", imageError);
            }
        } catch (error) {
            setError("Failed to upload images: " + error.message);
        } finally {
            setUploading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };




    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <SoftBox p={2}>
            <Grid container spacing={3}>
                {staff && (
                    <>
                        <Grid item xs={6} md={16} xl={6}>
                            <StaffInfo staff={staff}/>
                        </Grid>
                        <Grid item xs={6} md={16} xl={6}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        onClick={() => navigate(-1)}
                                        sx={{mr: 2}}
                                    >
                                        <ArrowBack/>
                                    </IconButton>
                                    <Box>
                                        <Typography variant="h4">
                                            {staff?.firstName} {staff?.lastName}s Photo Gallery
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {images.length} photos • Last
                                            updated: {images.length > 0 ? formatDate(images[0].uploadedAt) : "No photos"}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display="flex" gap={2}>
                                    <input
                                        accept="image/*"
                                        style={{display: "none"}}
                                        id="upload-staff-images"
                                        type="file"
                                        multiple
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                    />
                                    <label htmlFor="upload-staff-images">
                                        <Fab
                                            color="primary"
                                            component="span"
                                            disabled={uploading}
                                            size="medium"
                                        >
                                            {uploading ? <CircularProgress size={24}/> : <AddPhotoAlternate/>}
                                        </Fab>
                                    </label>
                                </Box>
                            </Box>
                            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                            {images.length === 0 ? (
                                <Card>
                                    <CardContent>
                                        <Box textAlign="center" py={4}>
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                No photos yet
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Upload photos to see them here
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Grid container spacing={2}>
                                    {images.map((image) => {
                                        return (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={image.imageId}
                                                  sx={{height: "auto"}}>
                                            <Card
                                                sx={{
                                                    cursor: "pointer",
                                                    transition: "transform 0.2s",
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    "&:hover": {
                                                        transform: "scale(1.02)",
                                                    },
                                                }}
                                                onClick={() => handleImageClick(image)}>
                                                <Box position="relative"
                                                     sx={{width: "100%", height: 200, backgroundColor: theme.palette.grey[100]}}>
                                                    <AuthenticatedImage
                                                        imagePath={image.imagePath}
                                                        imageUrl={image.publicImageUrl || image.imageUrl}
                                                        alt={`${staff?.firstName}`}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            display: "block",
                                                        }}
                                                        showLoading={false}
                                                    />
                                                    <Box
                                                        position="absolute"
                                                        top={8}
                                                        right={8}
                                                        display="flex"
                                                        gap={1}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "rgba(255,255,255,0.9)",
                                                                "&:hover": {
                                                                    backgroundColor: "rgba(255,255,255,1)",
                                                                },
                                                            }}
                                                            onClick={(e) => handleDeleteClick(image, e)}
                                                        >
                                                            <Delete fontSize="small"/>
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "rgba(255,255,255,0.9)",
                                                                "&:hover": {
                                                                    backgroundColor: "rgba(255,255,255,1)",
                                                                },
                                                            }}
                                                        >
                                                            <ZoomIn fontSize="small"/>
                                                        </IconButton>
                                                    </Box>
                                                    <Chip
                                                        label={formatDate(image.uploadedAt)}
                                                        size="small"
                                                        sx={{
                                                            position: "absolute",
                                                            bottom: 8,
                                                            left: 8,
                                                            backgroundColor: "rgba(0,0,0,0.7)",
                                                            color: "white",
                                                        }}
                                                    />
                                                </Box>
                                            </Card>
                                        </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                            <Dialog
                                open={Boolean(selectedImage)}
                                onClose={handleCloseDialog}
                                maxWidth="lg"
                                fullWidth
                            >
                                <DialogContent sx={{p: 0}}>
                                    {selectedImage && (
                                        <Box position="relative">
                                            <AuthenticatedImage
                                                imagePath={selectedImage.imagePath}
                                                imageUrl={selectedImage.publicImageUrl || selectedImage.imageUrl}
                                                alt="Full size preview"
                                                style={{
                                                    width: "100%",
                                                    height: "auto",
                                                    maxHeight: "80vh",
                                                    objectFit: "contain",
                                                }}
                                                showLoading={false}
                                            />
                                            <IconButton
                                                onClick={handleCloseDialog}
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    backgroundColor: "rgba(0,0,0,0.5)",
                                                    color: "white",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(0,0,0,0.7)",
                                                    },
                                                }}
                                            >
                                                <Close/>
                                            </IconButton>
                                        </Box>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseDialog}>Close</Button>
                                </DialogActions>
                            </Dialog>

                            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                                <DialogContent>
                                    <Typography variant="h6" gutterBottom>
                                        Delete Photo
                                    </Typography>
                                    <Typography>
                                        Are you sure you want to delete this photo? This action cannot be undone.
                                    </Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                                        Delete
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Grid>
                    </>)}
            </Grid>
        </SoftBox>
    );
}

export default StaffDetailPage;
